'use client';

// Marked client-only explicitly: everything exported here is a hook or is only ever called from one
// (`usePrefetchContents` uses `useEffect`). Without the directive that `useEffect` import lands in
// the server graph of every module that reaches this one — which includes `useCachedContentData`,
// whose `getCachedContentData` is awaited from server components. Same arrangement as
// `@cineamo/ui/src/hooks/useLocale`, which is why that one is safe to reach from the server path.
import { queryOptions, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useEffect } from 'react';

import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';
import { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';

import { ApiResponse } from '@cineamo/api/types/ApiResponse.types';

import { CACHE_24H } from '@cineamo/api/constants/caching-constants';

import { contentKeys } from '@cineamo/api/keys/query-keys';
import { getContentById } from '@cineamo/api/resources/content/content-api';

export type ContentWithMovie = ContentDto<{ cineamoMovie: MovieDto }>;

/**
 * Reusable query definition for a single content (movie wrapper) by id. Shared by
 * `useContent` and `usePrefetchContents` so the cache key, fetcher and caching
 * config stay in lock-step (CIN-11360).
 */
export function contentByIdQueryOptions(contentId: number | string | undefined) {
    return queryOptions({
        queryKey: contentKeys.detail(contentId ?? 'disabled'),
        // getContentById is typed loosely as ApiResponse<ContentDto>, but this endpoint always
        // embeds the movie. The previous inline useQuery asserted this via its return annotation;
        // with an extracted factory we assert it on the queryFn instead (behavior unchanged).
        queryFn: (): Promise<ApiResponse<ContentWithMovie>> =>
            getContentById(contentId!) as Promise<ApiResponse<ContentWithMovie>>,
        enabled: !!contentId,
        // Content metadata rarely changes — cache for 24h like the mobile app (content-by-id).
        staleTime: CACHE_24H,
        gcTime: CACHE_24H,
        refetchOnWindowFocus: false
    });
}

/**
 * Fetches a single content (movie wrapper) by id. Long staleTime since content
 * metadata rarely changes — once fetched, every component on the page reads from
 * the cache without round-tripping the API again.
 */
export function useContent(
    contentId: number | string | undefined
): UseQueryResult<ApiResponse<ContentWithMovie> | null> {
    return useQuery(contentByIdQueryOptions(contentId));
}

/**
 * Background-prefetches a batch of contents. Already-cached ids are skipped by
 * TanStack automatically. Replaces the `Promise.all(getContentById(...))` block
 * that used to live inside the showings infinite-query — that broke caching.
 */
export function usePrefetchContents(contentIds: (number | string)[]): void {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!contentIds.length) return;
        for (const id of contentIds) {
            if (id == null) continue;
            void queryClient.prefetchQuery(contentByIdQueryOptions(id));
        }
        // contentIds is expected to be deduped/memoized by the caller
    }, [contentIds, queryClient]);
}
